import { create } from "zustand";
import { persist, createJSONStorage, devtools } from "zustand/middleware";

import { CreateOrganizationInput, CreatePropertyInput, UserInvitationInput } from "graphql/generated/graphql";

// A file already uploaded to Storage and staged for the package.
export interface PlanReviewStagedDocument {
	localId: string;
	name: string;
	mimeType: string;
	size: number;
	publicUrl: string;
	docType: string;
}

// Selection made on the "Plan Review" setup screen and carried to the
// upload step. The plan review job is NOT created on the setup screen — a single
// action (analyze_plan_submittal) creates the job, stores the documents, and runs
// the whole analysis at once when the user submits on the upload step. This store
// only holds the chosen context between those two screens.
//
// Persisted to localStorage (like new-guide-form-store) so the selection survives a
// page refresh or a direct navigation to the upload step.
export interface PlanReviewSelection {
	organizationId: string;
	propertyId: string;
	jurisdictionId: string;
	// plan_review_submission_types.id. Empty when the selection comes from a project
	// created before the catalogue existed.
	submissionTypeId: string;
	propertyName: string;
	// Pre-staged files, set only when the user comes back from a submittal whose
	// analysis failed: it was never reviewed, so its package is shown for them to fix
	// rather than upload again. A revision starts from an empty upload step.
	documents?: PlanReviewStagedDocument[];
	// Organization / property the user created inside the flow but that are not in the
	// database yet. They travel with the selection and are persisted by
	// analyze_plan_submittal, in the same transaction as the job — nothing is created
	// if the user never submits the package. Same contract as start_inspection_job.
	// Files are already uploaded to Storage by then, so these stay serializable.
	newOrganization?: CreateOrganizationInput | null;
	newProperty?: CreatePropertyInput | null;
	newUser?: UserInvitationInput | null;
	// The applicant's answers to this jurisdiction + submission type's
	// ahj_fee_schedule_variables (MWA-788), read once by analyze_plan_submittal when the
	// project is created. Empty when the pair has no fee schedule (still free).
	feeVariableInputs?: { variable_key: string; value: number }[];
}

interface PlanReviewFlowState {
	selection: PlanReviewSelection | null;
	setSelection: (selection: PlanReviewSelection) => void;
	clearSelection: () => void;
}

const STORAGE_KEY = "plan-review-flow";

export const usePlanReviewFlowStore = create<PlanReviewFlowState>()(
	devtools(
		persist(
			set => ({
				selection: null,
				setSelection: selection =>
					set({ selection }, false, { type: "plan-review-flow/setSelection", payload: selection }),
				clearSelection: () => set({ selection: null }, false, "plan-review-flow/clearSelection")
			}),
			{
				name: STORAGE_KEY,
				storage: createJSONStorage(() => localStorage)
			}
		),
		{ name: "PlanReviewFlow", enabled: true }
	)
);
